/**
 * AuthService — Auth-Endpunkte über die http-Instanz.
 * Alle Methoden delegieren an den konfigurierten Axios-Client und den Token-Store.
 */

import http from './http'
import * as tokenStore from './tokenStore'
import type { PasswordLoginResult, VerifyOtpResult, AuthProfile, TrustedDevice } from '../types'

/**
 * Fordert einen OTP-Code für die gegebene E-Mail an.
 *
 * @param email E-Mail-Adresse des Benutzers
 * @returns Promise<void>
 */
async function requestOtp(email: string): Promise<void> {
  await http.post('/auth/request-otp', { email })
}

/**
 * Versucht Passwort-Login mit optionalem Trusted-Device-Token.
 *
 * @param email E-Mail-Adresse
 * @param password Passwort
 * @param deviceToken Optionales Trusted-Device-Token (Backend-Feld `deviceToken`)
 * @returns PasswordLoginResult (`{ token, account }` oder `{ otpRequired, email }`)
 */
async function passwordLogin(
  email: string,
  password: string,
  deviceToken?: string,
): Promise<PasswordLoginResult> {
  const payload: {
    email: string
    password: string
    deviceToken?: string
  } = { email, password }

  if (deviceToken !== undefined) {
    payload.deviceToken = deviceToken
  }

  return http.post('/auth/password', payload)
}

/**
 * Fordert einen Passwort-Reset per E-Mail an (admin/brand-admin).
 * Das Backend antwortet aus Datenschutzgründen immer mit { ok: true }.
 *
 * @param email E-Mail-Adresse des Kontos
 */
async function requestPasswordReset(email: string): Promise<void> {
  await http.post('/auth/request-password-reset', { email })
}

/**
 * Schließt einen Passwort-Reset mit Token (aus dem Mail-Link) ab.
 *
 * @param email E-Mail-Adresse des Kontos
 * @param token Reset-Token aus dem Mail-Link
 * @param newPassword Neues Passwort (min. 8 Zeichen)
 */
async function resetPassword(
  email: string,
  token: string,
  newPassword: string,
): Promise<void> {
  await http.post('/auth/reset-password', { email, token, newPassword })
}

/**
 * Verifiziert einen OTP-Code.
 *
 * @param input Objekt mit email, code und optionalem trustDevice-Flag
 * @returns VerifyOtpResult (JWT + ggf. Trusted-Device-Token)
 */
async function verifyOtp(input: {
  email: string
  code: string
  trustDevice?: boolean
}): Promise<VerifyOtpResult> {
  const payload = {
    email: input.email,
    code: input.code,
    trustDevice: input.trustDevice ?? false,
  }

  return http.post('/auth/verify-otp', payload)
}

/**
 * Holt die aktuelle Session des eingeloggten Benutzers.
 *
 * @returns Session-Objekt mit Benutzerdetails
 */
async function me(): Promise<AuthProfile> {
  return http.get('/auth/me')
}

// ===== Token-Store-Delegation =====

/**
 * Speichert ein JWT im lokalen Store.
 *
 * @param token JWT-String
 */
function setToken(token: string): void {
  tokenStore.setToken(token)
}

/**
 * Holt das gespeicherte JWT aus dem lokalen Store.
 *
 * @returns JWT-String oder null
 */
function getToken(): string | null {
  return tokenStore.getToken()
}

/**
 * Löscht das gespeicherte JWT aus dem lokalen Store.
 */
function clearToken(): void {
  tokenStore.clearToken()
}

/**
 * Speichert ein Vertrauensgerät im lokalen Store.
 *
 * @param device TrustedDevice-Objekt
 */
function setTrustedDevice(device: TrustedDevice): void {
  tokenStore.setTrustedDevice(device)
}

/**
 * Holt ein gültiges Vertrauensgerät, falls es noch nicht abgelaufen ist
 * und die E-Mail übereinstimmt.
 *
 * @param email E-Mail-Adresse zum Vergleich
 * @returns TrustedDevice oder null
 */
function getValidTrustedDevice(email: string): TrustedDevice | null {
  return tokenStore.getValidTrustedDevice(email)
}

/**
 * Löscht das gespeicherte Vertrauensgerät aus dem lokalen Store.
 */
function clearTrustedDevice(): void {
  tokenStore.clearTrustedDevice()
}

// ===== Export =====

export const AuthService = {
  requestOtp,
  passwordLogin,
  requestPasswordReset,
  resetPassword,
  verifyOtp,
  me,
  setToken,
  getToken,
  clearToken,
  setTrustedDevice,
  getValidTrustedDevice,
  clearTrustedDevice,
}
